import './index.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useIsFlagEmojiSupported, useFlagEmojiFont } from '@hh.ru/magritte-common-flag-emoji-font';
import { CellText, Cell, CellRightLabel } from '@hh.ru/magritte-ui-cell';
import { s as styles, C as CallingCodeInput } from './CallingCodeInput-gIrbwv6b.js';
import { Select } from '@hh.ru/magritte-ui-select';
import '@hh.ru/magritte-common-use-multiple-refs';
import '@hh.ru/magritte-ui-breakpoint';

const getFlagEmoji = (countryCode) => {
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
};
const renderSelectItem = ({ data, onChange, type }) => {
    if (type === 'delimiter') {
        return (jsx(CellText, { maxLines: 1, style: "secondary", children: data.text }));
    }
    return (jsx(Cell, { left: jsx("div", { className: styles.flagContainer, children: jsx("span", { className: classNames(styles.flagEmoji), children: getFlagEmoji(data.value) }) }), right: jsxs(CellRightLabel, { hideIcon: true, children: ["+", data.callingCode] }), horPadding: false, vertPadding: false, onClick: () => onChange(data, true), children: jsx(CellText, { children: data.text }) }));
};
const RegionSelect = forwardRef(({ selectDataProvider, selectOptions, country, callingCode, onChange, invalid, libPhoneNumberAPI, searchable, disabled, metadataVariant, }, ref) => {
    const { getCountryCallingCode } = libPhoneNumberAPI;
    const [inputValue, setInputValue] = useState(`+${callingCode}`);
    const exactMatchRef = useRef(true);
    const isFlagEmojiSupported = useIsFlagEmojiSupported();
    useFlagEmojiFont(metadataVariant, isFlagEmojiSupported);
    const pickerRefCallback = useCallback((node) => {
        if (node) {
            node.classList.toggle(styles.noFlagEmoji, !isFlagEmojiSupported);
        }
    }, [isFlagEmojiSupported]);
    useEffect(() => {
        setInputValue(`+${callingCode}`);
    }, [callingCode]);
    const value = useMemo(() => selectOptions.find((item) => item.value === country), [selectOptions, country]);
    return (jsx(Select, { type: "radio", dataProvider: selectDataProvider, value: value, onChange: (selectedValue) => {
            exactMatchRef.current = true;
            onChange?.(selectedValue.value, getCountryCallingCode(selectedValue.value));
        }, triggerProps: { size: 'large' }, widthEqualToActivator: false, searchable: searchable, renderMobileItem: renderSelectItem, renderDesktopItem: renderSelectItem, pickerRef: pickerRefCallback, renderTrigger: ({ setSearchValue, onFocus, onBlur, onKeyDown, ref: triggerRef }) => (jsx(CallingCodeInput, { ref: triggerRef, inputElementRef: ref, className: !isFlagEmojiSupported && styles.noFlagEmoji, flag: jsx("span", { className: classNames(styles.flagEmoji), children: getFlagEmoji(country) }), invalid: invalid, disabled: disabled, onChange: (value) => {
                if (value === '') {
                    setInputValue('+');
                    setSearchValue('');
                }
                if (!value.startsWith('+')) {
                    return;
                }
                const code = value.substring(1);
                let hasPartialMatch = false;
                const exactMatch = selectOptions.find((option) => {
                    if (!option.value) {
                        return false;
                    }
                    if (option.callingCode.startsWith(code)) {
                        hasPartialMatch = true;
                    }
                    return option.callingCode === code;
                });
                // выполнится даже если есть точное совпадение
                if (hasPartialMatch) {
                    exactMatchRef.current = false;
                    setInputValue(`+${code}`);
                    setSearchValue(code === '' ? '' : `+${code}`);
                }
                if (exactMatch) {
                    exactMatchRef.current = true;
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    onChange(exactMatch.value, exactMatch.callingCode);
                }
            }, value: inputValue, onFocus: () => {
                setSearchValue('');
                onFocus?.();
            }, onBlur: () => {
                if (!exactMatchRef.current) {
                    const code = inputValue.substring(1);
                    const bestMatch = selectOptions.find((option) => option?.callingCode.startsWith(code));
                    if (bestMatch && bestMatch.value) {
                        onChange(bestMatch.value, bestMatch.callingCode);
                        setInputValue(`+${bestMatch.callingCode}`);
                    }
                }
                onBlur?.();
            }, onKeyDown: onKeyDown })) }));
});
RegionSelect.displayName = 'RegionSelect';

export { RegionSelect };
//# sourceMappingURL=RegionSelect.js.map
